import React, { useEffect } from 'react';

/**
 * This component is heavily based on `WistiaVideo` from
 * https://github.com/zapier/zapier/blob/d6489ecb187b5e58ec400fde0f2bc8ead45fd435/packages/design-systems/design-system-support/src/components/WistiaVideo/WistiaVideo.js
 *
 * Wistia provides us with an easy-embed script tag, except you can't use
 * those in React! So this uses some good ol' fashioned vanilla JS
 * to create and append the script tag to the document after it has mounted.
 *
 */

const buildWistiaScriptEmbed = (videoId: string) => {
  const script1 = document.createElement('script');

  const mediaSrc = `https://fast.wistia.com/embed/medias/${videoId}.jsonp`;

  script1.src = mediaSrc;
  script1.async = true;

  const script2 = document.createElement('script');
  script2.src = 'https://fast.wistia.com/assets/external/E-v1.js';
  script2.async = true;

  const wrappingDiv = document.createElement('div');
  wrappingDiv.classList.add('wistia_responsive_padding');

  const responsiveWrapperDiv = document.createElement('div');
  responsiveWrapperDiv.classList.add('wista_responsive_wrapper');

  const wistiaEmbedSpan = document.createElement('span');
  wistiaEmbedSpan.classList.add(
    'wistia_embed',
    `wistia_async_${videoId}`,
    'videoFoam=true',
  );

  wistiaEmbedSpan.setAttribute(
    'style',
    'display:inline-block;height:100%;position:relative;width:100%',
  );

  responsiveWrapperDiv.appendChild(wistiaEmbedSpan);
  wrappingDiv.appendChild(responsiveWrapperDiv);
  return [script1, script2, wrappingDiv];
};

const embedWistiaVideo = (videoId: string, videoElement: HTMLElement) => {
  const [script1, script2, wrappingDiv] = buildWistiaScriptEmbed(videoId);

  videoElement.appendChild(script1);
  videoElement.appendChild(script2);
  videoElement.appendChild(wrappingDiv);

  return [script1, script2, wrappingDiv];
};

type Props = {
  videoId: string;
};

export const WistiaVideo = ({ videoId }: Props) => {
  const videoRef = React.createRef<HTMLDivElement>();

  useEffect(() => {
    if (document && document.body) {
      if (videoRef && videoRef.current && !videoRef.current.hasChildNodes()) {
        embedWistiaVideo(videoId, videoRef.current);
      }
    }
  }, [videoId, videoRef]);

  return <div ref={videoRef} />;
};
